import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { requestNotificationPermission, setupAlertTimers } from '@/lib/pwa';

interface AlertSettings {
  meal_alert_enabled: boolean;
  meal_alert_time: string;
  leave_alert_enabled: boolean;
  leave_alert_time: string;
  freshday_alert_enabled: boolean;
  freshday_alert_time: string;
}

/**
 * Polls user settings and schedules browser notifications
 * for meal/leave/freshday alerts. Reschedules when settings change
 * or at midnight (new day).
 */
export function useAlertNotifications() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();

  const { data: settings } = useQuery<AlertSettings>({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get('/settings');
      return res.data.data;
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  });

  // Request permission when any alert is enabled
  useEffect(() => {
    if (!settings) return;
    if (settings.meal_alert_enabled || settings.leave_alert_enabled || settings.freshday_alert_enabled) {
      requestNotificationPermission();
    }
  }, [settings?.meal_alert_enabled, settings?.leave_alert_enabled, settings?.freshday_alert_enabled]);

  // Schedule timers
  useEffect(() => {
    if (!settings) return;

    const cleanup = setupAlertTimers({
      mealAlertEnabled: settings.meal_alert_enabled,
      mealAlertTime: settings.meal_alert_time,
      leaveAlertEnabled: settings.leave_alert_enabled,
      leaveAlertTime: settings.leave_alert_time,
      freshdayAlertEnabled: settings.freshday_alert_enabled,
      freshdayAlertTime: settings.freshday_alert_time,
    });

    // Reschedule at midnight for the next day
    const now = new Date();
    const midnight = new Date(now);
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 5, 0); // 00:00:05 next day
    const msUntilMidnight = midnight.getTime() - now.getTime();

    const midnightTimer = setTimeout(() => {
      cleanup();
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    }, msUntilMidnight);

    return () => {
      cleanup();
      clearTimeout(midnightTimer);
    };
  }, [
    settings?.meal_alert_enabled,
    settings?.meal_alert_time,
    settings?.leave_alert_enabled,
    settings?.leave_alert_time,
    settings?.freshday_alert_enabled,
    settings?.freshday_alert_time,
  ]);
}
